// Shopify Cart API client — same-origin POST /cart/add.js (no CORS needed,
// the widget runs on the storefront). Replaces the previous WooCommerce
// add-to-cart AJAX. Properties prefixed with `_` are hidden in the checkout UI but
// visible in the Shopify Admin order details.
//
// Property naming (renamed 2026-05-28, drop the `_apb_` prefix):
//   _session_id           — was _apb_job_id; merges _apb_session_token (one id per session)
//   _ai_image_url         — was _apb_preview_url; watermarked preview shown to buyer
//   _ai_image_url_highres — was _apb_image_url_hd; full-res download URL
//   _style                — was _apb_style; style_id from the metafield
//   _portrait_mode        — was _apb_portrait_mode; AI model selector
//   _customer_email       — was _apb_customer_email; collected from EmailGate
//
// WebhookOrderPaidHandler MUST look for `_session_id` (not `_apb_job_id`)
// after this rename ships. Bridge update flagged in the response summary.

export interface CartLineItemProperties {
  _ai_image_url: string;
  _ai_image_url_highres: string;
  /** Set for AI-portrait orders (upload/preview flow). Absent for the Canvas
   *  upsell page, which has no generation session / style / model. */
  _session_id?: string;
  _style?: string;
  _portrait_mode?: string;
  _customer_email?: string;
  // Anything else the bridge or back office needs can be appended;
  // Shopify accepts arbitrary string keys.
  [key: string]: string | undefined;
}

/** @deprecated Renamed to CartLineItemProperties; kept as alias for one
 *  release to avoid breaking external imports. Remove next cleanup pass. */
export type ApbLineItemProperties = CartLineItemProperties;

export interface AddToCartItem {
  variantId: number;
  quantity?: number;
  properties: CartLineItemProperties;
}

export interface CartLineItem {
  id: number;
  variant_id: number;
  quantity: number;
  properties: Record<string, string>;
}

export interface AddToCartResponse {
  items: CartLineItem[];
}

export class CartError extends Error {
  readonly status: number;
  readonly shopifyDescription?: string;

  constructor(status: number, message: string, shopifyDescription?: string) {
    super(message);
    this.name = 'CartError';
    this.status = status;
    this.shopifyDescription = shopifyDescription;
  }
}

/**
 * POST /cart/add.js — adds one or more line items to the current cart.
 * Returns the Shopify response so callers can read the resulting line IDs.
 * Throws CartError on non-2xx (e.g., out-of-stock 422, validation 400).
 */
/**
 * POST that retries on Shopify's 429 (cart API throttle), honouring the
 * `Retry-After` header (capped). Shopify rate-limits bursts of /cart/* calls;
 * a couple of backed-off retries clears the transient 429 instead of erroring.
 */
async function postCart(url: string, init: RequestInit, retries = 2): Promise<Response> {
  let res = await fetch(url, init);
  for (let attempt = 0; res.status === 429 && attempt < retries; attempt++) {
    if (init.signal?.aborted) break;
    const retryAfter = parseFloat(res.headers.get('Retry-After') || '');
    const waitMs = Number.isFinite(retryAfter) && retryAfter > 0
      ? Math.min(retryAfter * 1000, 4000)
      : 700 * (attempt + 1);
    await new Promise((r) => setTimeout(r, waitMs));
    res = await fetch(url, init);
  }
  return res;
}

export async function addToCart(
  items: AddToCartItem[],
  signal?: AbortSignal,
): Promise<AddToCartResponse> {
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  // 1-item policy: empty the cart first so only this portrait is checked out
  // (a buyer who picks a second format shouldn't end up with two line items).
  // Best-effort — a failed clear must not block the purchase.
  try {
    await postCart('/cart/clear.js', { method: 'POST', headers: jsonHeaders, signal });
  } catch {
    /* ignore — proceed to add even if clear failed */
  }

  const body = {
    items: items.map((i) => ({
      id: i.variantId,
      quantity: i.quantity ?? 1,
      // Drop undefined entries so JSON doesn't emit `null` properties.
      properties: stripUndefined(i.properties),
    })),
  };

  const res = await postCart('/cart/add.js', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    let description: string | undefined;
    try {
      const data = await res.json();
      // Shopify cart errors come back like { status, message, description }
      description = data?.description || data?.message;
    } catch { /* ignore parse errors */ }
    // 429 survived the retries — give the buyer a clear, friendly nudge.
    if (res.status === 429) {
      // No description → proceedToCheckout's catch shows this friendly message.
      throw new CartError(429, 'Too many requests — please wait a moment and tap the button again.');
    }
    throw new CartError(res.status, `Add to cart failed (${res.status})`, description);
  }

  return res.json();
}

/**
 * Redirect to Shopify checkout. Navigate the TOP window, not the current frame:
 * when the storefront is loaded inside an iframe (Shopify theme editor preview),
 * driving the iframe to /checkout → shop.app is blocked by shop.app's
 * `frame-ancestors` CSP. Breaking out of the frame avoids that. Falls back to
 * the current window if `window.top` is cross-origin-restricted.
 */
export function goToCheckout(): void {
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = '/checkout';
      return;
    }
  } catch {
    /* cross-origin top — fall through to same-window navigation */
  }
  window.location.href = '/checkout';
}

function stripUndefined<T extends Record<string, string | undefined>>(
  obj: T,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'string' && v !== '') out[k] = v;
  }
  return out;
}
